import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Plus, Edit2, Trash2, Search, Upload, QrCode } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { useColumnVisibility, getDefaultVisibility } from '../../hooks/useColumnVisibility';
import { DataTable } from '../ui/data-table';
import EmployeeImport from './EmployeeImport';
import { EmployeeDialog } from './EmployeeDialog';
import { EmployeeQRCodeModal } from './EmployeeQRCodeModal';
import { toast } from 'sonner';
import { usePageSize } from '../../hooks/usePageSize';
import { useFilterStorage } from '../../hooks/useFilterStorage';
import {
  GET_EMPLOYEES_PAGINATED,
  GET_POSITIONS,
  GET_SHIFTS,
  GET_DEPARTMENTS,
  GET_CONTRACT_TYPES,
  CREATE_EMPLOYEE_MUTATION,
  UPDATE_EMPLOYEE_MUTATION,
} from '../../graphql/queries';
import { usePermission } from '../../hooks/usePermission';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  login: string;
  pin: string;
  employeeCode?: string;
  role: string;
  roleId?: string | null;
  roleName?: string | null;
  roleNames?: string[] | null;
  isActive: boolean;
  department?: string;
  position?: {
    id: string;
    name: string;
    department?: string;
  };
  hireDate: string;
  terminationDate?: string | null;
  autoCloseEnabled?: boolean;
  autoCloseType?: string;
  autoCloseTime?: string;
  autoCloseAfterHours?: number;
}

interface EmployeeManagementProps {
  currentUserRole?: string;
}

export const EmployeeManagement: React.FC<EmployeeManagementProps> = ({
  currentUserRole
}) => {
  const canCreate = usePermission('employees.create');
  const canEdit = usePermission('employees.edit');
  const canDelete = usePermission('employees.delete');
  const canBulkImport = usePermission('employees.bulkImport');
  const { pageSize: defaultPageSize } = usePageSize();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useFilterStorage<string>('employees:activeFilter', 'true');
  const [showImport, setShowImport] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedEmployeeForQR, setSelectedEmployeeForQR] = useState<Employee | null>(null);
  const [confirmToggleEmployee, setConfirmToggleEmployee] = useState<Employee | null>(null);
  const [confirmToggleLoading, setConfirmToggleLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'lastName', desc: false }
  ]);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when sorting or filter changes
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [sorting]);

  React.useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [activeFilter]);

  // Convert TanStack sorting to GraphQL format
  const sortBy = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  // Fetch employees with pagination
  const { data: employeesData, loading: employeesLoading, refetch: refetchEmployees } = useQuery(GET_EMPLOYEES_PAGINATED, {
    variables: {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy,
      sortOrder,
      searchTerm: debouncedSearchTerm || undefined,
      isActive: activeFilter === 'all' ? undefined : activeFilter === 'true',
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Employees loaded:", data.employeesPaginated?.employees?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading employees:", error);
      toast.error("Błąd podczas ładowania pracowników");
    },
  });

  // Fetch positions for dropdown
  const { data: positionsData } = useQuery(GET_POSITIONS, {
    onError: (error) => {
      console.error("❌ Error loading positions:", error);
    },
  });

  // Fetch shifts for dropdown
  const { data: shiftsData } = useQuery(GET_SHIFTS, {
    onError: (error) => {
      console.error("❌ Error loading shifts:", error);
    },
  });

  // Fetch departments for dropdown
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS, {
    onError: (error) => {
      console.error("❌ Error loading departments:", error);
    },
  });

  // Fetch contract types for dropdown
  const { data: contractTypesData } = useQuery(GET_CONTRACT_TYPES, {
    onError: (error) => {
      console.error("❌ Error loading contract types:", error);
    },
  });

  const [createEmployee] = useMutation(CREATE_EMPLOYEE_MUTATION);
  const [updateEmployee] = useMutation(UPDATE_EMPLOYEE_MUTATION);

  const employees = employeesData?.employeesPaginated?.employees || [];
  const totalEmployees = employeesData?.employeesPaginated?.total || 0;
  const pageCount = employeesData?.employeesPaginated?.totalPages || 0;
  const positions = positionsData?.positions || [];
  const shifts = shiftsData?.shifts || [];
  const departments = departmentsData?.departments || [];
  const contractTypes = (contractTypesData?.contractTypes || []).filter((ct: any) => ct.isActive);

  const handleSave = async (id: string | null, data: any) => {
    try {
      if (id) {
        await updateEmployee({
          variables: { id, data },
        });
        toast.success("Pracownik został zaktualizowany");
      } else {
        await createEmployee({
          variables: { data },
        });
        toast.success("Pracownik został dodany");
      }
      await refetchEmployees();
    } catch (error: any) {
      console.error("Error saving employee:", error);

      // Extract GraphQL error message
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message || "Błąd GraphQL");
      }

      // Extract network error message
      if (error.networkError) {
        throw new Error(`Błąd sieci: ${error.networkError.message}`);
      }

      throw new Error(error.message || "Błąd podczas zapisywania pracownika");
    }
  };

  const handleAddNew = () => {
    setSelectedEmployee(null);
    setDialogOpen(true);
  };

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleShowQR = (employee: Employee) => {
    setSelectedEmployeeForQR(employee);
    setQrModalOpen(true);
  };

  const handleCloseQRModal = () => {
    setQrModalOpen(false);
    setSelectedEmployeeForQR(null);
  };

  const handleRequestToggleActive = (employee: Employee) => {
    setConfirmToggleEmployee(employee);
  };

  const handleCancelToggleActive = () => {
    if (confirmToggleLoading) return;
    setConfirmToggleEmployee(null);
  };

  const handleConfirmToggleActive = async () => {
    if (!confirmToggleEmployee) return;
    const target = confirmToggleEmployee;
    const willDeactivate = target.isActive;
    setConfirmToggleLoading(true);
    try {
      await updateEmployee({
        variables: {
          id: target.id,
          data: { isActive: !target.isActive },
        },
      });
      await refetchEmployees();
      toast.success(
        willDeactivate
          ? 'Pracownik został dezaktywowany'
          : 'Pracownik został aktywowany'
      );
      setConfirmToggleEmployee(null);
    } catch (error: any) {
      console.error('Error toggling employee active state:', error);
      toast.error(
        error.message ||
          (willDeactivate
            ? 'Błąd podczas dezaktywacji pracownika'
            : 'Błąd podczas aktywacji pracownika')
      );
    } finally {
      setConfirmToggleLoading(false);
    }
  };

  // Define table columns — must be stable reference for visibility hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns: ColumnDef<Employee>[] = React.useMemo(() => [
    {
      accessorKey: "lastName",
      header: "Nazwisko",
      cell: ({ row }) => (
        <div className="font-medium min-w-[100px]">
          {row.original.lastName}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "firstName",
      header: "Imię",
      cell: ({ row }) => (
        <div className="font-medium min-w-[100px]">
          {row.original.firstName}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <div className="min-w-[150px]">{row.original.email}</div>,
      enableSorting: true,
      meta: { defaultHidden: true },
    },
    {
      accessorKey: "login",
      header: "Login",
      cell: ({ row }) => (
        <span className="font-mono text-sm bg-primary/10 text-primary px-2 py-1 rounded">
          {row.original.login}
        </span>
      ),
      enableSorting: false,
      meta: { defaultHidden: true },
    },
    {
      accessorKey: "pin",
      header: "PIN",
      cell: ({ row }) => (
        <span className="font-mono text-sm bg-secondary text-secondary-foreground px-2 py-1 rounded">
          {row.original.pin}
        </span>
      ),
      enableSorting: false,
      meta: { defaultHidden: true },
    },
    {
      accessorKey: "role",
      header: "Rola",
      cell: ({ row }) => {
        const legacyLabel = row.original.role === 'ADMIN' ? 'Administrator'
          : row.original.role === 'MANAGER' ? 'Kierownik'
          : 'Pracownik';
        const names = row.original.roleNames && row.original.roleNames.length > 0
          ? row.original.roleNames
          : [row.original.roleName || legacyLabel];
        const variantFor = (name: string): 'destructive' | 'default' | 'secondary' =>
          name === 'Administrator' ? 'destructive'
            : name === 'Kierownik' ? 'default'
            : 'secondary';
        return (
          <div className="flex flex-col items-center gap-1">
            {names.map((name) => (
              <Badge key={name} variant={variantFor(name)}>{name}</Badge>
            ))}
          </div>
        );
      },
      enableSorting: true,
      meta: { align: 'center' },
    },
    {
      accessorKey: "department",
      header: "Dział",
      cell: ({ row }) => row.original.department || '-',
      enableSorting: true,
    },
    {
      accessorKey: "position",
      header: "Stanowisko",
      cell: ({ row }) => row.original.position?.name || '-',
      enableSorting: true,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'destructive'}>
          {row.original.isActive ? 'Aktywny' : 'Nieaktywny'}
        </Badge>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "hireDate",
      header: "Data zatrudnienia",
      cell: ({ row }) => new Date(row.original.hireDate).toLocaleDateString('pl-PL'),
      enableSorting: false,
      meta: { defaultHidden: true },
    },
    {
      id: "actions",
      header: "Akcje",
      enableHiding: false,
      size: 120,
      meta: { align: 'center' },
      cell: ({ row }) => (
        <div className="flex space-x-2 whitespace-nowrap justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShowQR(row.original)}
            title="Pokaż kod QR"
          >
            <QrCode className="w-4 h-4" />
          </Button>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit(row.original)}
              title="Edytuj"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRequestToggleActive(row.original)}
              title={row.original.isActive ? 'Dezaktywuj' : 'Aktywuj'}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
      enableSorting: false,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const defaults = React.useMemo(() => getDefaultVisibility(columns), [columns]);
  const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnVisibility('employees', defaults);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Zarządzanie pracownikami</h2>
          <p className="text-muted-foreground mt-2">Dodawaj, edytuj i zarządzaj pracownikami</p>
        </div>
        {(canCreate || canBulkImport) && (
          <div className="flex space-x-2">
            {canCreate && (
              <Button
                onClick={handleAddNew}
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Dodaj pracownika</span>
              </Button>
            )}
            {canBulkImport && (
              <Button
                onClick={() => setShowImport(true)}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Import</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Import Form */}
      {showImport && (
        <EmployeeImport />
      )}

      {/* Search and Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Lista pracowników</CardTitle>
              <CardDescription>
                Wszyscy pracownicy w systemie ({totalEmployees} rekordów)
              </CardDescription>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <span className="text-sm text-muted-foreground mr-1">Status:</span>
                <Button
                  variant={activeFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveFilter('all')}
                >
                  Wszyscy
                </Button>
                <Button
                  variant={activeFilter === 'true' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveFilter('true')}
                >
                  Aktywni
                </Button>
                <Button
                  variant={activeFilter === 'false' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveFilter('false')}
                >
                  Nieaktywni
                </Button>
              </div>
              <div className="flex items-center space-x-2">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Szukaj pracowników..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={employees}
            pageCount={pageCount}
            pagination={pagination}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
            loading={employeesLoading}
            manualPagination={true}
            manualSorting={true}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onResetColumnVisibility={resetToDefaults}
          />
        </CardContent>
      </Card>

      <EmployeeDialog
        employee={selectedEmployee}
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
        positions={positions}
        shifts={shifts}
        departments={departments}
        contractTypes={contractTypes}
        currentUserRole={currentUserRole}
      />

      <EmployeeQRCodeModal
        employee={selectedEmployeeForQR}
        open={qrModalOpen}
        onClose={handleCloseQRModal}
      />

      <Dialog
        open={confirmToggleEmployee !== null}
        onOpenChange={(open) => {
          if (!open) handleCancelToggleActive();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmToggleEmployee?.isActive
                ? 'Deaktywuj pracownika'
                : 'Aktywuj pracownika'}
            </DialogTitle>
            <DialogDescription>
              {confirmToggleEmployee && (
                <>
                  {confirmToggleEmployee.isActive ? (
                    <>
                      Pracownik{' '}
                      <span className="font-medium text-foreground">
                        {confirmToggleEmployee.firstName}{' '}
                        {confirmToggleEmployee.lastName}
                      </span>{' '}
                      zostanie deaktywowany. Nie będzie mógł się zalogować
                      ani rejestrować zdarzeń, ale jego dane i historia
                      pozostaną zachowane. Możesz go ponownie aktywować w
                      każdej chwili.
                    </>
                  ) : (
                    <>
                      Pracownik{' '}
                      <span className="font-medium text-foreground">
                        {confirmToggleEmployee.firstName}{' '}
                        {confirmToggleEmployee.lastName}
                      </span>{' '}
                      zostanie ponownie aktywowany i będzie mógł się
                      zalogować oraz rejestrować zdarzenia.
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelToggleActive}
              disabled={confirmToggleLoading}
            >
              Anuluj
            </Button>
            <Button
              variant={
                confirmToggleEmployee?.isActive ? 'destructive' : 'default'
              }
              onClick={handleConfirmToggleActive}
              disabled={confirmToggleLoading}
            >
              {confirmToggleLoading
                ? 'Przetwarzanie...'
                : confirmToggleEmployee?.isActive
                ? 'Deaktywuj'
                : 'Aktywuj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};